import { isArray, type PushEventOptions, unknownString } from '@grafana/faro-core';

import type { CacheType, FaroNavigationTiming, FaroResourceTiming } from './types';

const w3cTraceparentFormat = /^00-[a-f0-9]{32}-[a-f0-9]{16}-[0-9]{1,2}$/;

type SpanContext = PushEventOptions['spanContext'];

// Extract traceparent from serverTiming, if present
export function getSpanContextFromServerTiming(serverTimings: PerformanceServerTiming[] = []): SpanContext | undefined {
  for (const serverEntry of serverTimings) {
    if (serverEntry.name === 'traceparent') {
      if (!w3cTraceparentFormat.test(serverEntry.description)) {
        continue;
      }

      const [, traceId, spanId] = serverEntry.description.split('-');
      if (traceId != null && spanId != null) {
        return { traceId, spanId };
      }

      break;
    }
  }

  return undefined;
}

/**
 * Absolute wall-clock timestamp (epoch ms) for a performance entry, given its
 * `startTime` (ms relative to the document time origin).
 *
 * We intentionally avoid `performance.timeOrigin + startTime`: `timeOrigin` is a
 * wall-clock value fixed at document creation, while `startTime` comes from the
 * monotonic clock behind `performance.now()`, which does not advance while the OS is
 * suspended (e.g. a sleeping laptop). After a suspend the two drift apart, so
 * `timeOrigin + startTime` backdates the entry by the suspended duration — observed in
 * the wild as hours to days, heavily concentrated on macOS. See
 * https://github.com/grafana/faro-web-sdk/issues/2179.
 *
 * Instead we re-derive the origin from the wall clock at emit time:
 * `Date.now() - performance.now()` is the current (drift-corrected) effective time
 * origin, so `Date.now() - (performance.now() - startTime)` places the entry at its real
 * wall-clock time. When no drift has occurred this equals `timeOrigin + startTime`, so
 * there is no behavioural change in the normal case. `Math.min(wallNow, …)` clamps the
 * rare entry that straddles a suspend (started before sleep, observed on wake) so it
 * cannot land in the future.
 */
export function performanceEntryTimestampMs(
  startTimeMs: number,
  clock: { wallNow: number; monoNow: number } = { wallNow: Date.now(), monoNow: performance.now() }
): number {
  const { wallNow, monoNow } = clock;
  return Math.min(wallNow, wallNow - (monoNow - startTimeMs));
}

export function performanceObserverSupported(): boolean {
  return 'PerformanceObserver' in window;
}

export function onDocumentReady(handleReady: () => void): void {
  if (document.readyState === 'complete') {
    handleReady();
  } else {
    const readyStateCompleteHandler = () => {
      if (document.readyState === 'complete') {
        handleReady();
        document.removeEventListener('readystatechange', readyStateCompleteHandler);
      }
    };

    document.addEventListener('readystatechange', readyStateCompleteHandler);
  }
}

type PerformanceEntryAllowProperties = Record<string, Array<string | number> | string | number>;

export function includePerformanceEntry(
  performanceEntryJSON: Record<string, any>,
  allowProps: PerformanceEntryAllowProperties = {}
): boolean {
  for (const [allowPropKey, allowPropValue] of Object.entries(allowProps)) {
    const perfEntryPropVal = performanceEntryJSON[allowPropKey];

    if (perfEntryPropVal == null) {
      return false;
    }

    if (isArray(allowPropValue)) {
      return allowPropValue.includes(perfEntryPropVal);
    }

    return perfEntryPropVal === allowPropValue;
  }

  // empty object allows all
  return true;
}

export function createFaroResourceTiming(resourceEntryRaw: PerformanceResourceTiming): FaroResourceTiming {
  const {
    connectEnd,
    connectStart,
    decodedBodySize,
    domainLookupEnd,
    domainLookupStart,
    duration,
    encodedBodySize,
    fetchStart,
    initiatorType,
    name,
    nextHopProtocol,
    redirectEnd,
    redirectStart,
    // @ts-expect-error the renderBlockingStatus property is not available in all browsers
    renderBlockingStatus: rbs,
    requestStart,
    responseEnd,
    responseStart,
    responseStatus,
    secureConnectionStart,
    transferSize,
    workerStart,
  } = resourceEntryRaw;

  return {
    name: name,
    httpHost: getHostFromUrl(name),
    duration: toFaroPerformanceTimingString(duration),
    tcpHandshakeTime: toFaroPerformanceTimingString(connectEnd - connectStart),
    dnsLookupTime: toFaroPerformanceTimingString(domainLookupEnd - domainLookupStart),
    tlsNegotiationTime: toFaroPerformanceTimingString(connectEnd - secureConnectionStart),
    responseStatus: toFaroPerformanceTimingString(responseStatus),
    redirectTime: toFaroPerformanceTimingString(redirectEnd - redirectStart),
    requestTime: toFaroPerformanceTimingString(responseStart - requestStart),
    responseTime: toFaroPerformanceTimingString(responseEnd - responseStart),
    fetchTime: toFaroPerformanceTimingString(responseEnd - fetchStart),
    // workerStart is 0 when the request is not intercepted by a service worker (W3C Resource Timing spec).
    // fetchStart is a timeOrigin-relative timestamp, not a duration, so guard against reporting it as the
    // service worker time when no service worker handled the request.
    serviceWorkerTime: toFaroPerformanceTimingString(workerStart > 0 ? fetchStart - workerStart : 0),
    decodedBodySize: toFaroPerformanceTimingString(decodedBodySize),
    encodedBodySize: toFaroPerformanceTimingString(encodedBodySize),
    cacheHitStatus: getCacheType(),
    renderBlockingStatus: toFaroPerformanceTimingString(rbs) as FaroResourceTiming['renderBlockingStatus'],
    protocol: nextHopProtocol,
    initiatorType: initiatorType,
    visibilityState: document.visibilityState,
    ttfb: toFaroPerformanceTimingString(responseStart - requestStart),
    transferSize: toFaroPerformanceTimingString(transferSize),

    // TODO: add in future iteration, ideally after nested objects are supported by the collector.
    // serverTiming: resourceEntryRaw.serverTiming,
  };

  function getCacheType(): CacheType {
    let cacheType: CacheType = 'fullLoad';
    if (transferSize === 0) {
      if (decodedBodySize > 0) {
        cacheType = 'cache';
      }
    } else {
      if (responseStatus != null) {
        if (responseStatus === 304) {
          cacheType = 'conditionalFetch';
        }
      } else if (encodedBodySize > 0 && transferSize < encodedBodySize) {
        cacheType = 'conditionalFetch';
      }
    }
    return cacheType;
  }
}

export function createFaroNavigationTiming(navigationEntryRaw: PerformanceNavigationTiming): FaroNavigationTiming {
  const {
    activationStart,
    domComplete,
    domContentLoadedEventEnd,
    domContentLoadedEventStart,
    domInteractive,
    fetchStart,
    loadEventEnd,
    loadEventStart,
    responseStart,
    type,
  } = navigationEntryRaw;

  const parserStart = getDocumentParsingTime();

  return {
    ...createFaroResourceTiming(navigationEntryRaw),
    pageLoadTime: toFaroPerformanceTimingString(domComplete - fetchStart),
    documentParsingTime: toFaroPerformanceTimingString(parserStart ? domInteractive - parserStart : null),
    domProcessingTime: toFaroPerformanceTimingString(domComplete - domInteractive),
    domContentLoadHandlerTime: toFaroPerformanceTimingString(domContentLoadedEventEnd - domContentLoadedEventStart),
    onLoadTime: toFaroPerformanceTimingString(loadEventEnd - loadEventStart),

    // For navigation entries we can calculate the TTFB based on activationStart. We overwrite the TTFB value coming with the resource entry.
    // For more accuracy on prerendered pages page we calculate relative top the activationStart instead of the start of the navigation.
    // clamp to 0 if activationStart occurs after first byte is received.
    ttfb: toFaroPerformanceTimingString(Math.max(responseStart - (activationStart ?? 0), 0)),
    type: type,
  };
}

function getDocumentParsingTime(): number | null {
  if (performance.timing?.domLoading != null) {
    // the browser is about to start parsing the first received bytes of the HTML document.
    // This property is deprecated but there isn't a really good alternative atm.
    // For now we stick with domLoading and keep researching a better alternative.
    return performance.timing.domLoading - performance.timeOrigin;
  }

  return null;
}

function getHostFromUrl(url: string): string {
  try {
    return new URL(url).host || unknownString;
  } catch {
    return unknownString;
  }
}

function toFaroPerformanceTimingString(v: unknown): string {
  if (v == null) {
    return unknownString;
  }

  if (typeof v === 'number') {
    return Math.round(v > 0 ? v : 0).toString();
  }

  return v.toString();
}
